import { useState, useEffect } from 'react';
import { MonthlyPlan } from '@/entities/MonthlyPlan';
import { useMonth } from '@/components/MonthContext';
import { useAccount } from '@/components/auth/AccountContext';
import { supabase } from '@/lib/supabase';

interface UseCurrentPlanReturn {
  currentPlan: MonthlyPlan | null;
  isLoading: boolean;
  error: string | null;
}

export const useCurrentPlan = (): UseCurrentPlanReturn => {
  const [currentPlan, setCurrentPlan] = useState<MonthlyPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { selectedMonth, selectedYear } = useMonth();
  const { currentAccountId } = useAccount();

  useEffect(() => {
    const loadCurrentPlan = async () => {
      if (!currentAccountId) {
        setCurrentPlan(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const plans = await MonthlyPlan.filter({
          year: selectedYear,
          month: selectedMonth + 1, // selectedMonth é 0-11, banco usa 1-12
          account_id: currentAccountId
        });
        
        let plan = plans[0] || null;

        // Fallback defensivo: caso o filter não retorne, fazer uma consulta direta
        if (!plan) {
          const { data, error } = await supabase
            .from('monthly_plan')
            .select(`
              *,
              planned_categories(*)
            `)
            .eq('year', selectedYear)
            .eq('month', selectedMonth + 1)
            .eq('account_id', currentAccountId)
            .order('created_at', { ascending: false })
            .limit(1);

          if (error) throw error;
          plan = (data && data.length > 0) ? (data[0] as any) : null;
        }

        setCurrentPlan(plan);
      } catch (err) {
        console.error('Erro ao carregar plano atual:', err);
        setError('Erro ao carregar plano atual');
        setCurrentPlan(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrentPlan();
  }, [selectedMonth, selectedYear, currentAccountId]);

  return {
    currentPlan,
    isLoading,
    error
  };
};
